import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { API_URL, formatDate } from '@/lib/utils';
import { Calendar, User, ArrowRight, Eye, BookOpen } from 'lucide-react';

const Blog = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const response = await axios.get(`${API_URL}/blogs?published_only=true`);
        setBlogs(response.data);
      } catch (error) {
        console.error('Error fetching blogs:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBlogs();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="blog-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-50 to-blue-50 py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <span className="text-[#2eb24c] font-semibold text-sm uppercase tracking-wider">Insights & Updates</span>
          <h1 className="font-manrope text-4xl md:text-5xl font-bold text-[#1a2a5e] mt-2 mb-6">
            Healthcare Blog
          </h1>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Stay updated with the latest healthcare industry news, accreditation tips, 
            and insights from our experts.
          </p>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          {blogs.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {blogs.map((blog) => (
                <Card key={blog.id} className="border-0 shadow-lg overflow-hidden blog-card">
                  <div className="aspect-video bg-gradient-to-br from-[#1a2a5e] to-[#3b7dd8] flex items-center justify-center">
                    {blog.image_url ? (
                      <img src={blog.image_url} alt={blog.title} className="w-full h-full object-cover" />
                    ) : (
                      <BookOpen className="h-16 w-16 text-white/50" />
                    )}
                  </div>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        {formatDate(blog.created_at)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Eye className="h-4 w-4" />
                        {blog.views} views
                      </span>
                    </div>
                    <span className="text-xs font-semibold text-[#2eb24c] bg-[#2eb24c]/10 px-2 py-1 rounded">
                      {blog.category}
                    </span>
                    <h3 className="font-manrope font-bold text-lg text-[#1a2a5e] mt-3 mb-2 line-clamp-2">
                      {blog.title}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4 line-clamp-3">{blog.excerpt}</p>
                    <div className="flex items-center justify-between pt-4 border-t">
                      <span className="flex items-center gap-2 text-sm text-gray-500">
                        <User className="h-4 w-4" />
                        {blog.author}
                      </span>
                      <Button asChild variant="ghost" size="sm" className="text-[#1a2a5e] hover:text-[#2eb24c]">
                        <Link to={`/blog/${blog.slug}`}>
                          Read More <ArrowRight className="ml-1 h-4 w-4" />
                        </Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <BookOpen className="h-20 w-20 text-gray-300 mx-auto mb-6" />
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Coming Soon</h3>
              <p className="text-gray-600 mb-8 max-w-md mx-auto">
                We're working on bringing you valuable healthcare insights and industry updates. 
                Check back soon!
              </p>
              <Button asChild className="bg-[#1a2a5e] hover:bg-[#2d4a8e]">
                <Link to="/contact">Contact Us for Updates</Link>
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-16 bg-[#1a2a5e] text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-manrope text-3xl font-bold mb-4">Stay Updated</h2>
          <p className="text-blue-100 mb-8">
            Subscribe to our newsletter for the latest healthcare industry updates and accreditation tips.
          </p>
          <Button asChild size="lg" className="bg-[#2eb24c] hover:bg-[#259a40]">
            <Link to="/contact">Subscribe Now</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Blog;
